/**
 * scans.js — Scan domain service.
 * Scan history, schedule CRUD, re-run, halt, pause, resume, delete, reimport.
 */
import {
  toApiUrl,
  toApiRootUrl,
  fetchWithAuthRetry,
  jsonFetchOptions,
  defaultHeaders,
  getCredentialsMode,
  parseJsonOrThrow,
  readApiErrorMessage,
  handleUnauthorizedResponse,
  normalizeApiList,
  compactObject,
  requestJson,
  extractAnyId,
  setNetworkHistoryEndpointUnavailable,
  getNetworkHistoryRequestPromise,
  setNetworkHistoryRequestPromise,
} from './_core';

export async function rerunScan(scanId, scanType, token = null) {
  const response = await fetchWithAuthRetry(
    toApiUrl('/scan/rerun/'),
    {
      ...jsonFetchOptions(token),
      method: 'POST',
      body: JSON.stringify({ scan_id: scanId, scan_type: scanType }),
    },
    token
  );
  if (!response.ok) {
    const detail = await readApiErrorMessage(response, 'Scan re-run');
    throw new Error(detail);
  }
  return parseJsonOrThrow(response, 'Scan re-run');
}

export async function haltNetworkScan(scanId, scannerId, token = null) {
  const response = await fetchWithAuthRetry(
    toApiUrl('/scan/halt/'),
    {
      ...jsonFetchOptions(token),
      method: 'POST',
      body: JSON.stringify({ scan_id: scanId, scanner_id: scannerId }),
    },
    token
  );
  if (!response.ok) {
    const detail = await readApiErrorMessage(response, 'Scan halt');
    throw new Error(detail);
  }
  return parseJsonOrThrow(response, 'Scan halt');
}

export async function pauseNetworkScan(scanId, scannerId, token = null) {
  const response = await fetchWithAuthRetry(
    toApiUrl('/scan/pause/'),
    {
      ...jsonFetchOptions(token),
      method: 'POST',
      body: JSON.stringify({ scan_id: scanId, scanner_id: scannerId }),
    },
    token
  );
  if (!response.ok) {
    const detail = await readApiErrorMessage(response, 'Scan pause');
    throw new Error(detail);
  }
  return parseJsonOrThrow(response, 'Scan pause');
}

export async function resumeNetworkScan(scanId, scannerId, token = null) {
  const response = await fetchWithAuthRetry(
    toApiUrl('/scan/resume/'),
    {
      ...jsonFetchOptions(token),
      method: 'POST',
      body: JSON.stringify({ scan_id: scanId, scanner_id: scannerId }),
    },
    token
  );
  if (!response.ok) {
    const detail = await readApiErrorMessage(response, 'Scan resume');
    throw new Error(detail);
  }
  return parseJsonOrThrow(response, 'Scan resume');
}

export async function deleteNetworkScans(scanIds, token = null) {
  const response = await fetchWithAuthRetry(
    toApiUrl('/scan/delete/'),
    {
      ...jsonFetchOptions(token),
      method: 'POST',
      body: JSON.stringify({ scan_ids: Array.isArray(scanIds) ? scanIds : [scanIds] }),
    },
    token
  );
  if (!response.ok) {
    const detail = await readApiErrorMessage(response, 'Scan delete');
    throw new Error(detail);
  }
  return parseJsonOrThrow(response, 'Scan delete');
}

export async function reimportNetworkScan(scanId, token = null) {
  const encodedScanId = encodeURIComponent(String(scanId || '').trim());
  const response = await fetchWithAuthRetry(
    toApiUrl(`/networkscan/reimport/?scan_id=${encodedScanId}`),
    {
      ...jsonFetchOptions(token),
      method: 'POST',
    },
    token
  );
  if (!response.ok) {
    const detail = await readApiErrorMessage(response, 'Scan re-import');
    throw new Error(detail);
  }
  return parseJsonOrThrow(response, 'Scan re-import');
}

export async function getRunningScans(token = null, query = null) {
  const qs =
    query && typeof query === 'object' && Object.keys(query).length > 0
      ? `?${new URLSearchParams(query).toString()}`
      : '';
  const response = await fetchWithAuthRetry(
    toApiUrl(`/dashboard/scans/running/${qs}`),
    jsonFetchOptions(token),
    token
  );
  if (!response.ok) {
    await handleUnauthorizedResponse(response);
    const detail = await readApiErrorMessage(response, `Running scans failed (${response.status})`);
    throw new Error(detail);
  }
  const data = await parseJsonOrThrow(response, 'Running scans');
  return normalizeApiList(data);
}

export async function getNetworkScanHistory(token = null, query = null, { force = false } = {}) {
  const existing = getNetworkHistoryRequestPromise();
  if (existing && !force) {
    try {
      const rows = await existing;
      return Array.isArray(rows) ? rows : [];
    } catch {
      return [];
    }
  }

  // If force=true, discard the stale in-flight promise so we always fetch fresh data.
  if (force && existing) {
    setNetworkHistoryRequestPromise(null);
  }

  const promise = (async () => {
  const qs =
    query && typeof query === 'object' && Object.keys(query).length > 0
      ? `?${new URLSearchParams(query).toString()}`
      : '';

    try {
      const response = await fetchWithAuthRetry(toApiUrl(`/networkscan/history/${qs}`), jsonFetchOptions(token), token);
      if (response.ok) {
        setNetworkHistoryEndpointUnavailable(false);
        const data = await parseJsonOrThrow(response, 'Network scan history');
        return normalizeApiList(data);
      }
      if (response.status >= 500 || response.status === 404) {
        setNetworkHistoryEndpointUnavailable(true);
      }
    } catch {
      /* primary endpoint failed */
    }

    return [];
  })();

  setNetworkHistoryRequestPromise(promise);

  try {
    const rows = await promise;
    return Array.isArray(rows) ? rows : [];
  } finally {
    setNetworkHistoryRequestPromise(null);
  }
}

export async function getNetworkScanVulnDetail(scanId, options = {}, token = null) {
  const id = String(scanId || '').trim();
  if (!id) {
    throw new Error('scan_id is required.');
  }
  const params = new URLSearchParams({ scan_id: id });
  const ip = String(options.ip || '').trim();
  if (ip) params.set('ip', ip);

  const response = await fetchWithAuthRetry(
    toApiUrl(`/networkscan/vulns/?${params.toString()}`),
    jsonFetchOptions(token),
    token
  );
  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Network scan detail failed (${response.status})`);
    throw new Error(msg);
  }
  return parseJsonOrThrow(response, 'Network scan vulnerability detail');
}

export async function getWebScanVulnDetail(scanId, token = null) {
  const id = String(scanId || '').trim();
  if (!id) {
    throw new Error('scan_id is required.');
  }
  const params = new URLSearchParams({ scan_id: id });
  const response = await fetchWithAuthRetry(
    toApiUrl(`/webscan/vulns/?${params.toString()}`),
    jsonFetchOptions(token),
    token
  );
  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Web scan detail failed (${response.status})`);
    throw new Error(msg);
  }
  return parseJsonOrThrow(response, 'Web scan vulnerability detail');
}

export async function getWebScanHistory(token = null, query = null) {
  const qs =
    query && typeof query === 'object' && Object.keys(query).length > 0
      ? `?${new URLSearchParams(query).toString()}`
      : '';
  const candidates = [
    `/webscan/history/${qs}`,
    `/webscan/results/${qs}`,
    `/webscan/results${qs}`,
  ];

  let lastServiceError = '';
  for (const path of candidates) {
    let response;
    try {
      response = await fetchWithAuthRetry(toApiUrl(path), jsonFetchOptions(token), token);
    } catch {
      continue;
    }

    if (response.ok) {
      const data = await parseJsonOrThrow(response, 'Web scan history');
      return normalizeApiList(data);
    }

    if (response.status === 401) {
      await handleUnauthorizedResponse(response);
      const detail = await readApiErrorMessage(response, `Web scan history (${response.status})`);
      throw new Error(detail);
    }

    if (response.status === 404) {
      continue;
    }

    if (response.status >= 500) {
      lastServiceError = await readApiErrorMessage(response, `Web scan history (${response.status})`);
      continue;
    }

    const detail = await readApiErrorMessage(response, `Web scan history (${response.status})`);
    throw new Error(detail);
  }

  if (lastServiceError) {
    throw new Error(lastServiceError);
  }

  throw new Error('Web scan history is not available right now. Please try again later.');
}

export function formatXspectreScheduleStartTime(startDateIso, timeHhmm) {
  const dm = String(startDateIso || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const tm = String(timeHhmm || '').trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!dm || !tm) return 'now';
  const y = parseInt(dm[1], 10);
  const mo = parseInt(dm[2], 10);
  const d = parseInt(dm[3], 10);
  let h = parseInt(tm[1], 10);
  let min = parseInt(tm[2], 10);
  if (![y, mo, d, h, min].every((n) => Number.isFinite(n))) return 'now';
  h = ((h % 24) + 24) % 24;
  min = Math.max(0, Math.min(59, min));
  const period = h >= 12 ? 'PM' : 'AM';
  let h12 = h % 12;
  if (h12 === 0) h12 = 12;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d)}/${pad(mo)}/${y} ${pad(h12)}:${pad(min)}:00 ${period}`;
}

export function buildNetworkScanScheduleRequestBody(payload) {
  if (!payload || typeof payload !== 'object') {
    throw new Error('buildNetworkScanScheduleRequestBody: payload object required');
  }
  const freq = String(payload.frequency || 'one-off').trim().toLowerCase();
  const scanTypeValue = String(payload.scanType || 'perimeter').trim().toLowerCase();
  const isPerimeterScanner = scanTypeValue === 'perimeter';
  return compactObject({
    site_uuid: String(payload.site || '').trim(),
    scanner_id: String(payload.scanner || '').trim(),
    target: String(payload.targets || '').trim(),
    start_time: formatXspectreScheduleStartTime(payload.startDate, payload.startTime),
    scan_freqency: freq || 'one-off',
    perimeter_scanner: isPerimeterScanner,
    scanner_deployment: isPerimeterScanner ? 'perimeter_scanner' : 'on_premise',
    exclude_ip: String(payload.exclude || '').trim(),
    custom_tcp_ports: String(payload.tcpPorts || '').trim(),
    custom_udp_ports: String(payload.udpPorts || '').trim(),
    scan_name: freq === 'one-off' ? '' : String(payload.scheduleName || '').trim(),
    client_timezone: payload.timezone || 'Australia/Melbourne',
    schedule_timezone: payload.timezone || undefined,
  });
}

export async function submitNetworkScanSchedule(payload, token = null, query = null) {
  const body = buildNetworkScanScheduleRequestBody(payload);
  const qs =
    query && typeof query === 'object' && Object.keys(query).length > 0
      ? `?${new URLSearchParams(query).toString()}`
      : '';
  const response = await fetchWithAuthRetry(toApiUrl(`/networkscan/schedule/${qs}`), {
    method: 'POST',
    ...jsonFetchOptions(token),
    body: JSON.stringify(body)
  }, token);
  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Network scan schedule failed (${response.status})`);
    throw new Error(msg);
  }
  return parseJsonOrThrow(response, 'Network scan schedule');
}

export function buildWebApplicationScanScheduleRequestBody(payload) {
  if (!payload || typeof payload !== 'object') {
    throw new Error('buildWebApplicationScanScheduleRequestBody: payload object required');
  }
  const freq = String(payload.frequency || 'one-off').trim().toLowerCase();
  const legacyFrequencyMap = {
    'one-off': 'One-Off',
    daily: 'Daily',
    weekly: 'Weekly',
    fortnightly: 'Fortnightly',
    monthly: 'Monthly - recurring date',
    quarterly: 'Quarterly',
  };
  return compactObject({
    url: String(payload.urls || '').trim(),
    site_uuid: String(payload.site || '').trim(),
    scanner_id: String(payload.scanner || '').trim(),
    start_time: formatXspectreScheduleStartTime(payload.startDate, payload.startTime),
    periodic_task_value: legacyFrequencyMap[freq] || 'One-Off',
    scanner_type: 'Web Scanner',
    scan_profile: payload.scanProfile ? (payload.scanProfile === 'deep' ? 'deep' : 'standard') : undefined,
    // Network & proxy: left out = the organization's scan network setting.
    egress_mode: payload.egressMode ? String(payload.egressMode).trim().toUpperCase() : undefined,
    egress_fallback: payload.egressFallback ? String(payload.egressFallback).trim().toUpperCase() : undefined,
    egress_proxy: payload.egressMode === 'BYO' && payload.egressProxy && !payload.egressProxyOnce ? payload.egressProxy : undefined,
    egress_proxy_once: payload.egressMode === 'BYO' && payload.egressProxyOnce ? payload.egressProxyOnce : undefined,
    egress_rotation: payload.egressMode === 'PROVIDER_POOL' && payload.egressRotation ? payload.egressRotation : undefined,
    // Login: left out = the site's saved login (on an edit, the current choice).
    scan_login: payload.scanLoginOnce || payload.scanLogin === null || payload.scanLogin === undefined ? undefined : payload.scanLogin,
    scan_login_once: payload.scanLoginOnce || undefined,
    scan_request_delay_ms:
      payload.requestDelayMs === '' || payload.requestDelayMs == null || !Number.isFinite(Number(payload.requestDelayMs))
        ? undefined
        : Math.trunc(Number(payload.requestDelayMs)),
    scan_name: freq === 'one-off' ? '' : String(payload.scheduleName || '').trim(),
    client_timezone: payload.timezone || 'Australia/Melbourne',
    schedule_timezone: payload.timezone || undefined,
  });
}

export async function submitWebApplicationScanSchedule(payload, token = null) {
  const body = buildWebApplicationScanScheduleRequestBody(payload);

  try {
    let response = await fetchWithAuthRetry(toApiUrl('/webscan/schedule/'), {
      method: 'POST',
      ...jsonFetchOptions(token),
      body: JSON.stringify(body),
    }, token);

    if (response.status === 404) {
      const formBody = new URLSearchParams();
      Object.entries(body).forEach(([key, value]) => {
        if (value != null) {
          formBody.set(key, String(value));
        }
      });

      response = await fetchWithAuthRetry(toApiRootUrl('/zapscanner/zap_scan/'), {
        method: 'POST',
        headers: {
          ...defaultHeaders(token),
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        },
        credentials: getCredentialsMode(token),
        cache: 'no-store',
        body: formBody.toString(),
      }, token);
    }
    
    if (!response.ok) {
      if (response.status >= 500) {
        throw new Error('Web scan service is temporarily unavailable. Please try again later or contact your administrator.');
      }
      if (response.status === 404) {
        throw new Error('Web scan is not available right now. Please contact your administrator.');
      }
      const msg = await readApiErrorMessage(response, 'Unable to schedule web scan. Please try again.');
      throw new Error(msg);
    }

    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      return parseJsonOrThrow(response, 'Web scan schedule');
    }

    return { ok: true };
  } catch (err) {
    if (err.message.includes('returned HTML') || err.message.includes('404')) {
      throw new Error('Web scan is not available right now. Please contact your administrator.');
    }
    throw err;
  }
}

export async function getNetworkScanSchedule(token = null, query = null) {
  const qs =
    query && typeof query === 'object' && Object.keys(query).length > 0
      ? `?${new URLSearchParams(query).toString()}`
      : '';
  const response = await fetchWithAuthRetry(toApiUrl(`/networkscan/schedule/${qs}`), jsonFetchOptions(token), token);

  if (!response.ok) {
    if (response.status === 404) {
      try {
        const detail = await parseJsonOrThrow(response, 'Network scan schedule');
        const noScans =
          typeof detail?.message === 'string' &&
          detail.message.toLowerCase().includes('no scans found');
        if (noScans) return [];
      } catch {
        // Ignore parse errors and fall through.
      }
    }

    const msg = await readApiErrorMessage(
      response,
      'Unable to load network scan schedules. Please try again.'
    );
    throw new Error(msg);
  }

  const data = await parseJsonOrThrow(response, 'Network scan schedule');
  return normalizeApiList(data);
}

export async function getWebScanSchedule(token = null, query = null) {
  const qs =
    query && typeof query === 'object' && Object.keys(query).length > 0
      ? `?${new URLSearchParams(query).toString()}`
      : '';
  const response = await fetchWithAuthRetry(toApiUrl(`/webscan/schedule/${qs}`), jsonFetchOptions(token), token);

  if (!response.ok) {
    if (response.status === 404 || response.status === 405) {
      return [];
    }
    if (response.status === 401) {
      await handleUnauthorizedResponse(response);
    }
    const msg = await readApiErrorMessage(
      response,
      'Unable to load web scan schedules. Please try again.'
    );
    throw new Error(msg);
  }

  const data = await parseJsonOrThrow(response, 'Web scan schedule');
  return normalizeApiList(data);
}

export const createNetworkScanSchedule = (payload, token = null) =>
  requestJson('POST', '/networkscan/schedule/', payload, token);

export async function updateNetworkScanSchedule(payload, token = null, query = null) {
  const uuId = extractAnyId({ ...(query || {}), ...(payload || {}) }, ['uu_id', 'scan_id', 'id']);
  if (!uuId) throw new Error('uu_id is required to update a schedule');
  const body =
    payload && typeof payload === 'object' && (payload.targets != null || payload.frequency != null)
      ? buildNetworkScanScheduleRequestBody(payload)
      : payload;
  return requestJson('PATCH', '/networkscan/schedule/', body, token, { uu_id: uuId });
}

export async function updateWebApplicationScanSchedule(payload, token = null, query = null) {
  const uuId = extractAnyId({ ...(query || {}), ...(payload || {}) }, ['uu_id', 'scan_id', 'id']);
  if (!uuId) throw new Error('uu_id is required to update a schedule');
  const body =
    payload && typeof payload === 'object' && (payload.urls != null || payload.frequency != null)
      ? buildWebApplicationScanScheduleRequestBody(payload)
      : payload;
  return requestJson('PATCH', '/webscan/schedule/', body, token, { uu_id: uuId });
}

export const deleteNetworkScanSchedule = (payload, token = null) => {
  const uuId = extractAnyId(payload, ['uu_id', 'scan_id', 'id']);
  if (!uuId) throw new Error('uu_id is required to delete a schedule');
  return requestJson('DELETE', '/networkscan/schedule/', null, token, { uu_id: uuId });
};

/** A site's API definition (OpenAPI URL or GraphQL endpoint) imported into web scans. */
export function getSiteApiDefinition(siteId, token = null) {
  return requestJson('GET', `/webscan/sites/${encodeURIComponent(siteId)}/api-definition/`, null, token);
}

export function updateSiteApiDefinition(siteId, payload = {}, token = null) {
  return requestJson('PUT', `/webscan/sites/${encodeURIComponent(siteId)}/api-definition/`, payload, token);
}

export function deleteSiteApiDefinition(siteId, token = null) {
  return requestJson('DELETE', `/webscan/sites/${encodeURIComponent(siteId)}/api-definition/`, null, token);
}

/** How web scans log in to a site (a token or session header). Secrets are write-only. */
export function getSiteScanLogin(siteId, token = null) {
  return requestJson('GET', `/webscan/sites/${encodeURIComponent(siteId)}/scan-login/`, null, token);
}

export function updateSiteScanLogin(siteId, payload = {}, token = null) {
  return requestJson('PUT', `/webscan/sites/${encodeURIComponent(siteId)}/scan-login/`, payload, token);
}

/** What the scan form may offer for a site. No secrets; anyone who can scan. */
export function getSiteScanLoginChoice(siteId, token = null) {
  return requestJson('GET', `/webscan/sites/${encodeURIComponent(siteId)}/scan-login/choice/`, null, token);
}

export function deleteSiteScanLogin(siteId, token = null) {
  return requestJson('DELETE', `/webscan/sites/${encodeURIComponent(siteId)}/scan-login/`, null, token);
}
